import { useRecoilValue } from 'recoil';
import { steppedHPConfigurations, getStepsFromVersioned, hangingProtocolStepIndex } from '../state';
import { FF, useSplitFlag } from 'modules/feature-flags';
import { useIsHPEditMode } from './useIsHPEditMode';
import { isAtLeastPhase4, isPhase4Steps } from '../../Viewer/StudiesBar/HangingProtocols/constants';
import type { HangingProtocolPhase } from '../../Viewer/StudiesBar/HangingProtocols/constants';

/**
 * Derives whether the viewer is in clinical step editing mode.
 *
 * Clinical step mode is active when:
 * - HP phase is phase-4-steps
 * - The current HP has stepped configurations
 * - The HP edit sidebar is NOT open
 */
export function useClinicalStepMode() {
  const [phase] = useSplitFlag<HangingProtocolPhase>(FF.HANGING_PROTOCOL_PHASE);
  const isPhase4 = isAtLeastPhase4(phase);
  const isHPStepsEnabled = isPhase4Steps(phase);
  // Check specifically for the HP edit sidebar, not any open sidebar — other sidebars (e.g. study
  // browser) must not be mistaken for HP edit mode and disable clinical step mode.
  const isHPEditMode = useIsHPEditMode();
  const isEditingHangingProtocol = isHPEditMode && isPhase4;
  const steppedHPVersioned = useRecoilValue(steppedHPConfigurations);
  const hasSteppedHP = getStepsFromVersioned(steppedHPVersioned) != null;
  const currentStepIndex = useRecoilValue(hangingProtocolStepIndex);

  const isClinicalStepMode = isHPStepsEnabled && hasSteppedHP && !isEditingHangingProtocol;

  return {
    isClinicalStepMode,
    isPhase4,
    isHPStepsEnabled,
    hasSteppedHP,
    isEditingHangingProtocol,
    currentStepIndex,
  };
}
